// cadence.ts — C7: pure schedule building for the CadencePicker.
//
// The user picks in LOCAL wall-clock time. Hermes evaluates cron in box time,
// and the box is UTC — so every cron the picker emits is converted here, and
// the day list is shifted when the conversion crosses midnight.
//
// SYNC: the four accept-regexes below mirror src/gateway/schedule-grammar.ts.
// Change one, change the other. (The picker validates the Advanced field live;
// the gateway validates again before anything reaches Hermes.)

export const ONCE_IN_RE = /^\d+[mh]$/
export const INTERVAL_RE = /^every \d+[mh]$/
export const ISO_AT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/
export const CRON_FIELD_RE = /^[\d*\-,/]+$/

export const MAX_SCHEDULE_LENGTH = 200

export type CadenceMode =
  | "daily"
  | "weekdays"
  | "weekends"
  | "specific"
  | "interval"
  | "once"
  | "advanced"

export interface CadenceState {
  mode: CadenceMode
  /** "HH:MM" in the user's local time — used by every cron mode. */
  time: string
  /** Local weekday numbers (0 = Sunday) for mode 'specific'. */
  days: number[]
  intervalAmount: number
  intervalUnit: "m" | "h"
  /** datetime-local value, "YYYY-MM-DDTHH:MM", in the user's local time. */
  onceAt: string
  /** Raw Hermes grammar, for mode 'advanced'. */
  raw: string
}

export interface BuiltSchedule {
  schedule: string
  display: string
  warning?: string
}

export const DEFAULT_CADENCE: CadenceState = {
  mode: "daily",
  time: "09:00",
  days: [1],
  intervalAmount: 30,
  intervalUnit: "m",
  onceAt: "",
  raw: "",
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const

/**
 * Minutes EAST of UTC (CEST = +120), i.e. the negation of
 * `Date.prototype.getTimezoneOffset()`. Passed in rather than read here so
 * buildSchedule stays pure and host-timezone independent.
 */
export function currentTzOffsetMinutes(at: Date = new Date()): number {
  return -at.getTimezoneOffset()
}

/** Validate a raw Hermes-grammar string — the mirror of the gateway's rules. */
export function validateRawSchedule(value: string): { ok: true } | { ok: false; reason: string } {
  const s = value.trim()
  if (!s) return { ok: false, reason: "Enter a schedule." }
  if (s.length > MAX_SCHEDULE_LENGTH) return { ok: false, reason: "That schedule is too long." }
  if (ONCE_IN_RE.test(s) || INTERVAL_RE.test(s)) return { ok: true }
  if (ISO_AT_RE.test(s)) {
    return Number.isNaN(Date.parse(`${s}Z`)) ? { ok: false, reason: "That is not a real date." } : { ok: true }
  }
  const fields = s.split(/\s+/)
  if (fields.length === 5 && fields.every((f) => CRON_FIELD_RE.test(f))) return { ok: true }
  return { ok: false, reason: 'Try "30m", "every 2h", a 5-field cron, or "2026-02-03T14:00".' }
}

/**
 * Turn picker state into the pair MUSE stores: the Hermes-grammar string
 * (UTC) and the plain-English summary (the user's own local wording).
 *
 * Pure. `tzOffsetMinutes` is captured at creation and never re-derived —
 * which is exactly why cron modes carry a DST warning.
 */
export function buildSchedule(state: CadenceState, tzOffsetMinutes: number): BuiltSchedule {
  switch (state.mode) {
    case "interval": {
      const amount = Math.trunc(state.intervalAmount)
      if (!Number.isFinite(amount) || amount <= 0) {
        return { schedule: "", display: "", warning: "Choose how often it should repeat." }
      }
      const unitWord = state.intervalUnit === "h" ? "hour" : "minute"
      return {
        schedule: `every ${amount}${state.intervalUnit}`,
        display: `Every ${amount} ${unitWord}${amount === 1 ? "" : "s"}`,
      }
    }

    case "once": {
      const parsed = parseLocalDateTime(state.onceAt)
      if (!parsed) return { schedule: "", display: "", warning: "Pick a date and time." }
      const utc = new Date(parsed.getTime() - tzOffsetMinutes * 60_000)
      return {
        schedule: formatUtcMinuteStamp(utc),
        display: `Once on ${state.onceAt.replace("T", " at ")}`,
      }
    }

    case "advanced": {
      const raw = state.raw.trim()
      const validation = validateRawSchedule(raw)
      if (!validation.ok) return { schedule: "", display: "", warning: validation.reason }
      return { schedule: raw, display: raw }
    }

    default:
      return buildCron(state, tzOffsetMinutes)
  }
}

function buildCron(state: CadenceState, tzOffsetMinutes: number): BuiltSchedule {
  const parsedTime = parseHHMM(state.time)
  if (!parsedTime) return { schedule: "", display: "", warning: "Pick a time." }

  const localDays = localDaysFor(state)
  if (localDays.length === 0) return { schedule: "", display: "", warning: "Pick at least one day." }

  const localMinutes = parsedTime.hour * 60 + parsedTime.minute
  const rawUtcMinutes = localMinutes - tzOffsetMinutes
  // Day shift when the conversion crosses midnight in either direction.
  const dayShift = Math.floor(rawUtcMinutes / 1440)
  const utcMinutes = ((rawUtcMinutes % 1440) + 1440) % 1440
  const utcHour = Math.floor(utcMinutes / 60)
  const utcMinute = utcMinutes % 60

  const utcDays = [...new Set(localDays.map((d) => (((d + dayShift) % 7) + 7) % 7))].sort((a, b) => a - b)

  return {
    schedule: `${utcMinute} ${utcHour} * * ${formatDayField(utcDays)}`,
    display: `${describeDays(state, localDays)} at ${state.time}`,
    // Honest, and deliberately unconditional for wall-clock repeats: the
    // offset is captured once, and no offset value can tell us whether this
    // user's region observes DST (UTC+0 does, UTC+5:30 does not).
    warning: "Summer/winter time shifts this by an hour — edit it in spring/autumn.",
  }
}

function localDaysFor(state: CadenceState): number[] {
  switch (state.mode) {
    case "daily":
      return [0, 1, 2, 3, 4, 5, 6]
    case "weekdays":
      return [1, 2, 3, 4, 5]
    case "weekends":
      return [0, 6]
    case "specific":
      return [...new Set(state.days.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6))].sort((a, b) => a - b)
    default:
      return []
  }
}

function formatDayField(days: number[]): string {
  const key = days.join(",")
  if (key === "0,1,2,3,4,5,6") return "*"
  if (key === "1,2,3,4,5") return "1-5"
  if (key === "0,6") return "0,6"
  return key
}

function describeDays(state: CadenceState, localDays: number[]): string {
  switch (state.mode) {
    case "daily":
      return "Every day"
    case "weekdays":
      return "Weekdays"
    case "weekends":
      return "Weekends"
    default:
      return localDays.map((d) => DAY_NAMES[d]).join(", ")
  }
}

function parseHHMM(value: string): { hour: number; minute: number } | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!m) return null
  const hour = Number(m[1])
  const minute = Number(m[2])
  if (hour > 23 || minute > 59) return null
  return { hour, minute }
}

/**
 * Parse a datetime-local value as if it were UTC. The caller then applies the
 * offset — doing it this way keeps the function independent of the host's own
 * timezone, so a test run in Berlin and one in UTC agree.
 */
function parseLocalDateTime(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value.trim())
  if (!m) return null
  const [, y, mo, d, h, mi] = m
  const ms = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi))
  if (Number.isNaN(ms)) return null
  return new Date(ms)
}

/** "YYYY-MM-DDTHH:MM" from a Date's UTC fields — the shape Hermes accepts. */
function formatUtcMinuteStamp(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0")
  return (
    `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`
  )
}
